/**
 * ConfigurationPage — `/ivt/admin/configuration`.
 *
 * Two sections, matching what the sync actually reads:
 *   Scope            — the date conditions and the tag prefixes
 *   Industry mapping — each industry's sales-hierarchy nodes, plus adding / deleting an industry
 *
 * Both commit as ONE transaction. That is not a UI convenience: the backend versions the
 * whole page with a single revision, and a split save would leave a state where the
 * window moved and the industry nodes did not — a state the next cron would use for real.
 *
 * A third tab, Access, is outside that transaction: grants save per call (see AccessTab).
 *
 * Admin-only, including the read. The nav item is hidden for non-admins and the service
 * returns 403 regardless; this page does not re-check the group, because putting
 * authorization in two places is what the backend's write-permission design exists to
 * avoid.
 *
 * Built from Cloudscape components like the rest of /ivt — Modal brings the focus trap,
 * Esc-to-close and dialog semantics that a hand-rolled overlay does not, and FormField
 * carries the label/description/error wiring that keeps the form reachable by keyboard
 * and screen reader.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Modal,
  PropertyFilter,
  SpaceBetween,
  Spinner,
  Tabs,
  TokenGroup,
} from '@amzn/awsui-components-react';
// Divider lives in the Core package, not awsui-components-react. Both are already
// dependencies of this app.
import { Divider } from '@amzn/awsui-components-console';
import type {
  HierarchyNode,
  IndustryNodeMapping,
  SyncScopeConfiguration,
} from '../../client/IvtHubClient';
import {
  fetchSyncScope,
  isRevisionConflict,
  saveSyncScope,
  type SyncScopeSnapshot,
} from '../../client/configurationClient';
import { denialHint } from '../../utils/authorization';
import FloatingLabelField from '../../components/FloatingLabelField/FloatingLabelField';
import { PROPERTY_FILTER_I18N } from '../../components/pipelineFilterShared';
import { diffSyncScope, hasWidening, type ChangeLine } from './syncScopeDiff';
import {
  RECORD_TYPE_OPTIONS,
  RECORD_TYPE_PROPERTIES,
  queryToRecordTypes,
  recordTypesToQuery,
} from './recordTypes';
import {
  FILTERING_PROPERTIES,
  nodesToQuery,
  queryToNodes,
} from './industryNodeQuery';
import {
  EMPTY_CATALOG,
  fetchHierarchyNodes,
  type HierarchyNodeCatalog,
} from './hierarchyNodeCatalog';
import {
  BOUNDARY_PROPERTIES,
  DATE_COLUMNS,
  columnHasConditions,
  describeUnsupported,
  normalizeColumnQuery,
  toPropertyFilterQuery,
} from './syncWindowQuery';
import { AddIndustryModal } from './AddIndustryModal';
import { DeleteIndustryModal } from './DeleteIndustryModal';
import { AccessTab } from './AccessTab';
import './ConfigurationPage.css';

/** Deep-enough clone for editing: every mutated level is replaced, never shared. */
function cloneConfig(c: SyncScopeConfiguration): SyncScopeConfiguration {
  return {
    ...c,
    // Rebuilt per column so an edit to one row cannot alias another's tokens.
    dateConditions: Object.fromEntries(
      Object.entries(c.dateConditions ?? {}).map(([col, q]) => [
        col,
        { ...q, tokens: (q?.tokens ?? []).map((t) => ({ ...t })) },
      ]),
    ),
    recordTypes: c.recordTypes ? [...c.recordTypes] : c.recordTypes,
    tagPrefixes: [...(c.tagPrefixes ?? [])],
    industries: (c.industries ?? []).map((m) => ({
      ...m,
      nodes: (m.nodes ?? []).map((n) => ({ ...n })),
    })),
  };
}

export default function ConfigurationPage() {
  const [saved, setSaved] = useState<SyncScopeSnapshot | null>(null);
  const [draft, setDraft] = useState<SyncScopeConfiguration | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [staleAfterConflict, setStaleAfterConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState<ChangeLine[] | null>(null);
  const [tab, setTab] = useState('scope');
  const [catalog, setCatalog] = useState<HierarchyNodeCatalog>(EMPTY_CATALOG);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const snap = await fetchSyncScope();
      setSaved(snap);
      setDraft(cloneConfig(snap.config));
      setStaleAfterConflict(false);
      setSaveError(null);
    } catch (e) {
      setLoadError(denialHint(e, 'en') ?? 'Could not load the configuration.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Separate from load(), and its failure is swallowed rather than surfaced: the candidates
  // only make the picker easier, so losing them must not stop an admin editing the scope.
  // EMPTY_CATALOG then leaves the control typable with no candidates and no false hints.
  useEffect(() => {
    let live = true;
    fetchHierarchyNodes()
      .then((c) => live && setCatalog(c))
      .catch(() => live && setCatalog(EMPTY_CATALOG));
    return () => {
      live = false;
    };
  }, []);

  const changes = useMemo(
    () => (saved && draft ? diffSyncScope(saved.config, draft) : []),
    [saved, draft],
  );

  // Shown on the tab labels so edits made on the tab you are not looking at are still
  // visible before you publish them.
  const scopeChangeCount = changes.filter((c) => c.scope === 'scope').length;
  const industryChangeCount = changes.filter((c) => c.scope === 'industry').length;

  /** Anything left unsaved, on either tab. Drives the banner and both buttons. */
  const dirty = changes.length > 0;

  /**
   * What the service would reject, checked here so Publish is refused rather than failing on
   * a rule the user never saw. An empty record-type list is the case that reads as a
   * narrowing that worked and actually syncs nothing.
   */
  const invalid = (draft?.recordTypes ?? []).length === 0;

  // Adding or deleting an industry bumps the revision and reloads the page, which would throw
  // the unsaved edits away.
  const industryWriteBlocked = dirty ? 'Publish or discard your changes first.' : undefined;

  const mutate = (f: (d: SyncScopeConfiguration) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      f(next);
      return next;
    });
  };

  if (loadError) {
    return (
      <Box padding="l">
        <Alert
          type="error"
          header="Could not load"
          action={<Button onClick={() => void load()}>Retry</Button>}
        >
          {loadError}
        </Alert>
      </Box>
    );
  }
  if (!saved || !draft) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner size="large" /> Loading…
      </Box>
    );
  }

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          description={`Last changed by ${saved.updatedBy ?? '—'}${
            saved.updatedAt
              ? ` · ${saved.updatedAt.toISOString().slice(0, 16).replace('T', ' ')}`
              : ''
          }`}
        >
          Admin
        </Header>

        {/* One banner for the whole page, because Publish commits the whole page. Naming
            which tab the edits are on is the point — the action bar is at the bottom and
            the other tab's edits are off-screen, so "you have unsaved changes" without a
            location leaves the user hunting. */}
        {dirty && (
          <Alert type="info" header="Unsaved changes">
            {[
              scopeChangeCount > 0 ? `${scopeChangeCount} on Scope` : null,
              industryChangeCount > 0 ? `${industryChangeCount} on Industry mapping` : null,
            ]
              .filter(Boolean)
              .join(', ')}
            . Publish at the bottom of the page to apply them, or Discard changes to put
            everything back.
          </Alert>
        )}

        {saveError && (
          <Alert
            type="error"
            header="Could not save"
            // A conflict is not a validation problem — nothing the user typed is wrong,
            // someone else committed first. Offer the only action that helps.
            action={
              staleAfterConflict ? <Button onClick={() => void load()}>Reload</Button> : undefined
            }
          >
            {saveError}
          </Alert>
        )}

        {/* Two tabs, ONE transaction. The action bar and the diff stay outside the tabs
            on purpose: the service versions the whole page with a single revision, so a
            per-tab save button would promise something the backend cannot do. The
            per-tab change counts exist for the same reason — someone on the scope tab
            would otherwise publish industry edits they never saw. */}
        <Tabs
          activeTabId={tab}
          onChange={({ detail }) => setTab(detail.activeTabId)}
          tabs={[
            {
              id: 'scope',
              label:
                scopeChangeCount > 0 ? `Scope (${scopeChangeCount})` : 'Scope',
              content: (
                <SpaceBetween size="l">
        {/* Separate from the scoring scope below because they gate different stages:
            this section decides what is written into opportunity_cache at all, the tag
            prefix only decides which of those rows get an activity score. */}
        <Container
          header={
            <Header
              variant="h2"
              description="Decides which opportunities are synced into IVT. An opportunity must match the dates AND one of the record types, and this whole section is AND’d with the Industry mapping tab."
            >
              Sync scope
            </Header>
          }
        >
          <SpaceBetween size="l">
            {/* Conditions within a row are AND'd, rows are OR'd. The "and" Divider below is what
                makes the grouping readable — without it the rows read as
                "d1 or d2 or (d3 and record type)" by operator precedence. */}
            <SpaceBetween size="l">
            {DATE_COLUMNS.map((column, i) => (
              <SpaceBetween key={column} size="xxs">
                {i > 0 && (
                  <Box color="text-status-inactive" fontSize="body-s">
                    or
                  </Box>
                )}
                <FloatingLabelField label={column}>
                  <PropertyFilter
                    query={toPropertyFilterQuery(draft.dateConditions?.[column])}
                    onChange={({ detail }) =>
                      mutate((d) => {
                        d.dateConditions = {
                          ...d.dateConditions,
                          [column]: normalizeColumnQuery(detail),
                        };
                      })
                    }
                    filteringProperties={BOUNDARY_PROPERTIES}
                    filteringOptions={[]}
                    // Always AND within a row, so the combinator is not offered.
                    hideOperations
                    disableFreeTextFiltering
                    expandToViewport
                    filteringPlaceholder="Set Criteria"
                    i18nStrings={{
                      ...PROPERTY_FILTER_I18N,
                      filteringAriaLabel: `Set the date conditions for ${column}`,
                      clearFiltersText: 'Clear',
                    }}
                  />
                </FloatingLabelField>
                {!columnHasConditions(draft.dateConditions ?? {}, column) && (
                  // Empty is not "unrestricted": a column with no conditions contributes
                  // nothing to the OR. Those read alike and mean opposite things.
                  <Box color="text-status-inactive" fontSize="body-s">
                    No conditions — this column admits nothing (it does NOT mean “unrestricted”)
                  </Box>
                )}
              </SpaceBetween>
            ))}

            {describeUnsupported(draft.dateConditions ?? {}).length > 0 && (
              <Alert type="warning" header="Some conditions the service cannot run">
                <ul>
                  {describeUnsupported(draft.dateConditions ?? {}).map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </Alert>
            )}
            </SpaceBetween>

            {/* Spans the full width, so it separates the whole date group from what follows
                rather than only the last row. */}
            <Divider>and</Divider>

            <SpaceBetween size="xxs">
              <FloatingLabelField label="record_type">
                <PropertyFilter
                  query={recordTypesToQuery(draft.recordTypes)}
                  onChange={({ detail }) =>
                    mutate((d) => {
                      d.recordTypes = queryToRecordTypes(detail);
                    })
                  }
                  filteringProperties={RECORD_TYPE_PROPERTIES}
                  filteringOptions={RECORD_TYPE_OPTIONS}
                  // Types are always OR'd, so the combinator would offer what the service ignores.
                  hideOperations
                  disableFreeTextFiltering
                  expandToViewport
                  filteringPlaceholder="Set Criteria"
                  i18nStrings={{
                    ...PROPERTY_FILTER_I18N,
                    filteringAriaLabel: 'Set the opportunity record types to sync',
                    clearFiltersText: 'Clear',
                  }}
                />
              </FloatingLabelField>
              {invalid && (
                // An allowlist, so empty admits nothing — the same trap as a date column with
                // no conditions, and worded to match.
                <Box color="text-status-inactive" fontSize="body-s">
                  No record types — this admits nothing (it does NOT mean “all types”)
                </Box>
              )}
            </SpaceBetween>
          </SpaceBetween>
        </Container>

        {/* ── (b) Activity-scoring scope ─────────────────────────────────
            One field today, and it stays its own section anyway: the tag prefix
            gates a different stage from everything above it, and grouping it with
            the sync window is what made people read it as a sync-volume control. */}
        <Container
          header={
            <Header
              variant="h2"
              description="Among the opportunities already synced, decides which get an activity score computed. Changes neither the number of rows synced nor the relevance-judgement volume."
            >
              Activity-scoring scope
            </Header>
          }
        >
          <SpaceBetween size="l">
            <FormField
              label="Tag prefixes"
              description="Interpolated into tag_name ILIKE '<prefix>%' to pick the opportunities whose activity score is computed — i.e. the ones already tagged into IVT scope."
            >
              <SpaceBetween size="xs">
                <TokenGroup
                  items={(draft.tagPrefixes ?? []).map((p) => ({ label: p, dismissLabel: `Remove ${p}` }))}
                  onDismiss={({ detail }) =>
                    mutate((d) => {
                      const list = [...(d.tagPrefixes ?? [])];
                      list.splice(detail.itemIndex, 1);
                      d.tagPrefixes = list;
                    })
                  }
                />
                <TagPrefixInput
                  onAdd={(value) =>
                    mutate((d) => {
                      if (!(d.tagPrefixes ?? []).includes(value)) {
                        d.tagPrefixes = [...(d.tagPrefixes ?? []), value];
                      }
                    })
                  }
                />
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Container>
                </SpaceBetween>
              ),
            },
            {
              id: 'industry',
              label:
                industryChangeCount > 0 ? `Industry mapping (${industryChangeCount})` : 'Industry mapping',
              content: (
                // Also part of the sync scope, not a separate gate — it is the other half
                // of the mirror's WHERE. Its own tab only because it is a per-industry
                // table rather than a handful of fields.
                <SpaceBetween size="l">
                    <Header
                      variant="h2"
                      actions={
                        <Button
                          disabled={!!industryWriteBlocked}
                          disabledReason={industryWriteBlocked}
                          onClick={() => setAdding(true)}
                        >
                          Add industry
                        </Button>
                      }
                      description="The other half of the sync scope, AND’d with Sync scope. A node is level=name, and mapping to a node includes its whole subtree. An empty list matches zero opportunities — it does not mean “match everything”."
                    >
                      Industry mapping
                    </Header>
                    {/* One card per industry, not nested in a page-level container. */}
                    {(draft.industries ?? []).map((m, idx) => (
                        <IndustryRow
                          key={m.industry}
                          mapping={m}
                          catalog={catalog}
                          deleteBlocked={industryWriteBlocked}
                          onDelete={() => setDeleting(m.industry ?? null)}
                          onNodesChange={(nodes) =>
                            mutate((d) => {
                              const target = d.industries?.[idx];
                              if (target) target.nodes = nodes;
                            })
                          }
                        />
                    ))}
                </SpaceBetween>
              ),
            },
            {
              id: 'access',
              label: 'Access',
              content: <AccessTab />,
            },
          ]}
        />

        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={!dirty} onClick={() => setDraft(cloneConfig(saved.config))}>
                Discard changes
              </Button>
              <Button
                variant="primary"
                disabled={!dirty || invalid || saving || staleAfterConflict}
                onClick={() => setConfirming(changes)}
              >
                Publish
              </Button>
            </SpaceBetween>
          }
        />
      </SpaceBetween>

      {adding && (
        <AddIndustryModal
          existingIndustries={(saved.config.industries ?? []).map((m) => m.industry ?? '')}
          onDismiss={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void load();
          }}
        />
      )}
      {deleting && (
        <DeleteIndustryModal
          industry={deleting}
          onDismiss={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      <Modal
        visible={confirming !== null}
        onDismiss={() => setConfirming(null)}
        header="Confirm publish"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={saving} onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={saving}
                onClick={async () => {
                  setSaving(true);
                  setSaveError(null);
                  try {
                    const next = await saveSyncScope(draft, saved.revision);
                    setSaved(next);
                    setDraft(cloneConfig(next.config));
                    setConfirming(null);
                  } catch (e) {
                    if (isRevisionConflict(e)) {
                      setStaleAfterConflict(true);
                      setSaveError(
                        'Someone else changed this configuration, so what you are looking at is not the latest version. Reload before editing again.',
                      );
                    } else {
                      setSaveError(denialHint(e, 'en') ?? 'Could not save.');
                    }
                    setConfirming(null);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Publish
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {confirming && hasWidening(confirming) && (
            <Alert type="warning" header="This change widens the scope">
              Newly admitted opportunities enter relevance judging. Every (opportunity × asset)
              pair is one paid call, and this <strong>cannot be undone</strong>.
            </Alert>
          )}
          <ul>
            {(confirming ?? []).map((c) => (
              <li key={c.text}>
                {c.widening ? (
                  <Box variant="strong" color="text-status-error">
                    {c.text}
                  </Box>
                ) : (
                  c.text
                )}
              </li>
            ))}
          </ul>
        </SpaceBetween>
      </Modal>
    </Box>
  );
}

/** Local input so a half-typed prefix never lands in the draft. */
function TagPrefixInput({ onAdd }: { onAdd: (value: string) => void }) {
  const [value, setValue] = useState('');
  // Mirrors the service's @pattern. Enforced here only to explain the rule before a
  // round-trip — the service is the authority, and it rejects `%` because a LIKE
  // wildcard would widen the dispatch scope to every tag without erroring.
  const valid = /^[A-Za-z0-9-]+$/.test(value);
  return (
    <FormField
      errorText={
        value !== '' && !valid
          ? 'Letters, digits and hyphens only (% and _ are LIKE wildcards).'
          : undefined
      }
      secondaryControl={
        <Button
          disabled={!valid}
          onClick={() => {
            onAdd(value);
            setValue('');
          }}
        >
          Add
        </Button>
      }
    >
      <Input
        value={value}
        placeholder="GCR-IVT-"
        ariaLabel="New tag prefix"
        onChange={({ detail }) => setValue(detail.value)}
      />
    </FormField>
  );
}

/**
 * One industry's nodes, edited as a PropertyFilter — the `Set Criteria` + `level=name`
 * token shape the prototype drew.
 *
 * Deliberately assembled the same way as the Motions and Impact pipeline filters:
 * `FloatingLabelField` for the notched title, and the SHARED `PROPERTY_FILTER_I18N`
 * rather than a local subset. A partial i18n object is why this first looked like a
 * different control — Cloudscape renders a missing key as blank, so the group headings
 * and the "Edit filter" popover buttons came out unlabelled.
 *
 * Flat, not collapsible: there are ~10 industries and the page exists to compare them.
 */
function IndustryRow({
  mapping,
  catalog,
  deleteBlocked,
  onDelete,
  onNodesChange,
}: {
  mapping: IndustryNodeMapping;
  catalog: HierarchyNodeCatalog;
  deleteBlocked?: string;
  onDelete: () => void;
  onNodesChange: (nodes: HierarchyNode[]) => void;
}) {
  const nodes = mapping.nodes ?? [];
  // Hint only, never blocked: the list can lag SFDC by a refresh cycle, so blocking would make
  // a newly added node unconfigurable. The copy says "not in the list" rather than "does not
  // exist", which would be a false claim.
  const notInCatalog = nodes.filter((n) => !catalog.isKnown(n.level, n.name));

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <Button
              variant="inline-link"
              iconName="remove"
              disabled={!!deleteBlocked}
              disabledReason={deleteBlocked}
              onClick={onDelete}
            >
              Delete industry
            </Button>
          }
        >
          {mapping.industry}
        </Header>
      }
    >
      <SpaceBetween size="m">
      <FloatingLabelField label="Nodes">
        {/* Wrapper is the CSS hook — see ConfigurationPage.css. */}
        <div className="ivt-cfg-node-picker">
          <PropertyFilter
            query={nodesToQuery(nodes)}
            onChange={({ detail }) => onNodesChange(queryToNodes(detail))}
            filteringProperties={FILTERING_PROPERTIES}
            // Candidates come from the service, so the common case is selectable and
            // searchable. Typing still works, which is needed here: the list can lag SFDC, and
            // it is empty when the fetch failed, so a node outside it must remain configurable.
            filteringOptions={catalog.options}
            // Nodes are always OR'd by the backend, so an and/or control would offer a
            // combinator the service ignores.
            hideOperations
            // A node must name a level; a free-text token could not become one.
            disableFreeTextFiltering
            // No expandToViewport, unlike the app's other property filters: it portals the
            // dropdown out of this subtree, and the wrapper above has to contain it for the
            // width rule to apply.
            filteringPlaceholder="Set Criteria"
            i18nStrings={{
              ...PROPERTY_FILTER_I18N,
              filteringAriaLabel: `Set the sales-hierarchy nodes for ${mapping.industry ?? ''}`,
              // Overridden here only. On Motions/Impact the control filters a list, so
              // "Clear filters" is accurate; here the tokens ARE the configuration, and
              // "filters" would name something this page does not have.
              clearFiltersText: 'Clear',
            }}
          />
        </div>
      </FloatingLabelField>
      {nodes.length === 0 && (
        <Box color="text-status-inactive" fontSize="body-s">
          No nodes mapped — matches zero opportunities
        </Box>
      )}
      {notInCatalog.length > 0 && (
        <Alert type="info">
          {notInCatalog.map((n) => `${n.level}=${n.name}`).join(', ')} is not in the node list
          {catalog.syncedAt ? ` (last refreshed ${catalog.syncedAt.toISOString().slice(0, 10)})` : ''}
          . That is expected for a node SFDC added since; if you typed it, check the spelling — a
          typo does not error, it just matches zero opportunities.
        </Alert>
      )}
      </SpaceBetween>
    </Container>
  );
}
